import { useTenant } from "@/providers/session";
import { useMutation } from "@apollo/client";
import { Checkbox } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuExternalLink } from "react-icons/lu";
import { toast } from "sonner";

import { RERUN_ACCOUNTING_PIPELINE } from "@/apis/gql/apolloClient";
import type { Supplier } from "@/apis/gql/types";
import { ValidationErrorMessage } from "@/components/shared/form/shared/form-validation";
import { LangfuseLink } from "@/components/shared/miscellaneous/langfuse-link";
import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { TagsSelect } from "@/components/shared/select-inputs/tags-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select as SelectComponent,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { TypographyP, TypographySmall } from "@/components/ui/typography";
import { InvoiceType } from "@/lib/constants/invoice-type";
import { InvoiceReviewType } from "@/lib/constants/invoices";
import { useUpdateInvoice } from "@/queries/invoices/update";
import {
  UpdateInvoiceFields,
  useInvoiceReviewContext,
  useInvoiceReviewStateDispatch,
  useSaveInvoice,
} from "../ReviewProvider";
import { InvoiceCoreInfo } from "../components/invoice-core-info";
import { SetAccountConfidencesCheckbox } from "../components/set-account-confidences-checkbox";
import SupplierSelectField from "./SupplierSelectField";

export enum ReviewModes {
  TASK = "TASK",
  COMPLETE = "COMPLETE",
}

interface Props {
  reviewMode: ReviewModes;
  onTagUpdate: (tags: string[]) => void;
  disabled?: boolean;
  readOnly?: string | React.ReactNode | boolean;
}

export default function InvoiceCoreInfoForm({
  reviewMode,
  onTagUpdate,
  disabled,
  readOnly,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State
  const {
    invoiceReviewState,
    refetch,
    updateBox,
    tags,
    tagsLoading,
    tagsUpdateError,
  } = useInvoiceReviewContext();

  const [rerunAccountingPipeline] = useMutation(RERUN_ACCOUNTING_PIPELINE);

  const { invoiceData } = invoiceReviewState;

  const dispatch = useInvoiceReviewStateDispatch();

  // Save Hooks
  const { saveInvoice } = useSaveInvoice();

  const newSupplier = () => {
    if (!invoiceData?.supplier) return false;
    if (!invoiceData?.supplier?.status) return true;
    if (invoiceData?.supplier?.status === "NEW") return true;

    return false;
  };
  // Local State
  const [fieldChanged, setFieldChanged] = React.useState(false);

  const { companySlug } = useTenant();

  const handleInvoiceSave = async (value: any, field: UpdateInvoiceFields) => {
    //tbd whether we actually need to check for fieldChanged here or can take it out.
    if (!fieldChanged && field != UpdateInvoiceFields.OcrReviewed) return;
    setFieldChanged(false);
    await saveInvoice(field, { [field]: value });
  };

  const handleOcrReviewedChange = async (checked: boolean) => {
    await handleInvoiceSave(checked, UpdateInvoiceFields.OcrReviewed);
    refetch();
  };

  const handleSupplierChange = async (supplier: Supplier | null) => {
    setFieldChanged(true);

    try {
      if (supplier) {
        dispatch({
          type: UpdateInvoiceFields.SupplierChange,
          payload: supplier,
        });
        await saveInvoice(UpdateInvoiceFields.SupplierChange, {
          supplierId: supplier.supplierId,
        });
      } else {
        dispatch({
          type: UpdateInvoiceFields.SupplierDelete,
          payload: {},
        });
      }
    } catch (error) {}
  };

  const handleReviewTypeChange = (value: string) => {
    dispatch({
      type: UpdateInvoiceFields.ReviewType,
      payload: value,
    });
    saveInvoice(UpdateInvoiceFields.ReviewType, {
      [UpdateInvoiceFields.ReviewType]: value,
    });
  };

  const invoiceTags = useMemo(() => {
    return invoiceData?.tags?.map(({ tagId }) => tagId) ?? [];
  }, [invoiceData?.tags]);

  const invoiceTagsOptions = useMemo(() => {
    return tags?.map(({ tagId, tagName }) => ({
      value: tagId,
      label: tagName,
    }));
  }, [tags]);

  const [updateInvoice] = useUpdateInvoice();

  if (!invoiceData) return null;

  const handleUpdateBookingStatus = async (isBooking: boolean) => {
    updateInvoice({
      invoiceId: invoiceData.invoiceId,
      isBooking,
    });
  };

  const handleRerunAccountingPipeline = async () => {
    try {
      const { data } = await rerunAccountingPipeline({
        variables: { invoiceId: invoiceData.invoiceId },
      });
      if (data.rerunAccountingPipeline) {
        toast.success(t("generic_success"));
      }
    } catch (error) {
      toast.error(t("generic_error"));
    }
  };

  let supplierConfidence = null;
  if (
    invoiceData?.supplierMatchConfidence === 1 &&
    invoiceData?.supplier?.status === "CONFIRMED"
  )
    supplierConfidence = 1;
  else if (invoiceData?.supplierMatchConfidence === 0) supplierConfidence = 0;

  const invoiceType = invoiceData.type;

  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <SuperAdminOnly>
        <div className="col-span-3">
          <TypographyP className="font-bold">Super Admin Only</TypographyP>
        </div>
        <div className="col-span-9" />

        <div className="col-span-3">
          <TypographySmall>OCR Geprüft</TypographySmall>
        </div>
        <div className="col-span-9">
          <Checkbox
            sx={{ p: 0 }}
            checked={invoiceData?.ocrReviewed}
            onChange={(event) => {
              handleOcrReviewedChange(event.target.checked);
            }}
          />
        </div>
        <SetAccountConfidencesCheckbox />
        <div className="col-span-3">
          <TypographySmall>Konten und BUs erneut auswählen</TypographySmall>
        </div>
        <div className="col-span-9">
          <Button onClick={handleRerunAccountingPipeline}>Go</Button>
        </div>
        <div className="col-span-3">
          <TypographySmall>{t("invoice_values.review_type")}</TypographySmall>
        </div>
        <div className="col-span-9">
          <SelectComponent
            value={invoiceData.reviewType}
            onValueChange={(value) => {
              handleReviewTypeChange(value);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(InvoiceReviewType).map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectComponent>
        </div>
        <div className="col-span-3">
          <TypographySmall>Tags</TypographySmall>
        </div>
        <div className="col-span-9">
          <TagsSelect
            error={tagsUpdateError}
            disabled={tagsLoading}
            options={invoiceTagsOptions}
            values={invoiceTags}
            onSelect={onTagUpdate}
          />
        </div>
        <div className="col-span-3">
          <TypographySmall>Booking</TypographySmall>
        </div>
        <div className="col-span-9">
          <SelectComponent
            value={invoiceData.isBooking ? "true" : "false"}
            onValueChange={(value) => {
              handleUpdateBookingStatus(value === "true");
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key="true" value="true">
                true
              </SelectItem>
              <SelectItem key="false" value="false">
                false
              </SelectItem>
            </SelectContent>
          </SelectComponent>
        </div>
        <div className="col-span-3">
          <TypographySmall>Langfuse</TypographySmall>
        </div>
        <div className="col-span-9">
          <LangfuseLink invoiceId={invoiceData.invoiceId} />
        </div>
        <div className="col-span-3">
          <TypographySmall>Invoice Type</TypographySmall>
        </div>
        <div className="col-span-9">
          <TypographySmall>{invoiceType}</TypographySmall>
        </div>
        <div className="col-span-12">
          <Separator />
        </div>
      </SuperAdminOnly>
      {supplierConfidence && reviewMode === ReviewModes.TASK ? null : (
        <>
          <div className="col-span-3">
            <div className="flex flex-row gap-1 items-center">
              <TypographySmall>
                {invoiceType === InvoiceType.RECEIVABLE
                  ? t("debtors.title")
                  : t("creditors.title")}
              </TypographySmall>
              <div className="flex flex-col items-start">
                {newSupplier() && <Badge>{t("new")}</Badge>}
              </div>
            </div>
          </div>
          <div className="col-span-9">
            <div className="flex flex-row gap-2">
              <div className="flex-1 max-w-[70%]">
                <SupplierSelectField
                  type={
                    invoiceType === InvoiceType.RECEIVABLE
                      ? "debtor"
                      : "creditor"
                  }
                  disabled={disabled}
                  readOnly={readOnly}
                  handleSupplierChange={handleSupplierChange}
                />
                <ValidationErrorMessage name="supplierId" />
              </div>
              {invoiceData?.supplierId && (
                <button
                  onClick={() => {
                    navigate({
                      to:
                        invoiceType === InvoiceType.RECEIVABLE
                          ? "/$company/buchhaltung/einstellungen/debitoren"
                          : "/$company/buchhaltung/einstellungen/kreditoren",
                      params: { company: companySlug },
                      search: { focusedRowId: invoiceData.supplierId } as any,
                    });
                  }}
                  className="text-sm text-foreground flex items-center gap-2 px-2 py-1 rounded hover:bg-muted"
                >
                  <LuExternalLink className="h-4 w-4" />
                  {t("edit_supplier")}
                </button>
              )}
            </div>
          </div>
        </>
      )}
      <div className="col-span-12">
        <InvoiceCoreInfo
          key={invoiceData.invoiceId}
          invoice={invoiceData}
          disabled={disabled}
          readOnly={readOnly}
          onFieldFocus={({ field }) =>
            updateBox(invoiceData?.invoicePolygon?.polygonData, field)
          }
          onFieldBlur={() => updateBox()}
          reviewMode={reviewMode}
        />
      </div>
    </div>
  );
}
